<app-page-breadcrumb pageTitle="Tipos de Cambio"></app-page-breadcrumb>

<div class="space-y-5">
  <p class="text-sm text-gray-500 dark:text-gray-400">
    Configure la moneda base del negocio y los tipos de cambio usados para resolver los precios de productos por moneda.
  </p>

  @if (error) {
    <div class="p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
      <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  }

  <!-- Moneda base -->
  <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
    <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Moneda base</h3>
    <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
      Es la moneda de referencia del negocio; su tipo de cambio siempre es 1.
      @if (baseCurrency) {
        <span class="ml-1 font-medium text-gray-700 dark:text-gray-300">
          Actual: {{ baseCurrency.currencyName }} ({{ baseCurrency.currencyCode }}) {{ baseCurrency.currencySymbol }}
        </span>
      } @else {
        <span class="ml-1 font-medium text-warning-600 dark:text-warning-400">Aún no configurada</span>
      }
    </p>
    <div class="flex flex-wrap items-end gap-3">
      <div class="min-w-[220px]">
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Moneda</label>
        <select [(ngModel)]="selectedBaseCurrencyId"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option [ngValue]="null" disabled>Seleccione una moneda</option>
          @for (c of currencies; track c.id) {
            <option [ngValue]="c.id">{{ c.name }} ({{ c.code }})</option>
          }
        </select>
      </div>
      <button type="button" (click)="guardarBase()" [disabled]="!selectedBaseCurrencyId || savingBase"
        class="px-4 py-2 text-sm rounded-lg font-medium bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed">
        {{ savingBase ? 'Guardando...' : 'Guardar moneda base' }}
      </button>
    </div>
  </div>

  <!-- Tipos de cambio -->
  <div class="flex flex-wrap items-center justify-between gap-3">
    <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-200">Tipos de cambio</h3>
    <button type="button" (click)="nuevaTasa()"
      class="px-3 py-1.5 text-sm rounded-lg font-medium bg-brand-500 text-white hover:bg-brand-600 flex items-center gap-1.5">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Nuevo tipo de cambio
    </button>
  </div>

  <!-- Form inline crear/editar -->
  @if (showForm) {
    <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
      <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
        {{ editing ? 'Editar tipo de cambio' : 'Nuevo tipo de cambio' }}
      </h4>

      @if (formError) {
        <div class="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
          <p class="text-sm text-error-600 dark:text-error-400">{{ formError }}</p>
        </div>
      }

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Moneda</label>
          @if (editing) {
            <input type="text" [value]="getCurrencyLabel(formCurrencyId!)" disabled
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" />
          } @else {
            <select [(ngModel)]="formCurrencyId"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              <option [ngValue]="null" disabled>Seleccione una moneda</option>
              @for (c of monedasDisponibles; track c.id) {
                <option [ngValue]="c.id">{{ c.name }} ({{ c.code }})</option>
              }
            </select>
          }
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tipo de cambio (a la base)</label>
          <input type="number" [(ngModel)]="formRateToBase" min="0" step="0.0001" placeholder="Ej. 3.75"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
        </div>
        <div class="flex items-center">
          <label class="inline-flex items-center gap-2 mt-5 cursor-pointer">
            <input type="checkbox" [(ngModel)]="formIsActive"
              class="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
            <span class="text-sm text-gray-700 dark:text-gray-300">Activa</span>
          </label>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 mt-4">
        <button type="button" (click)="cerrarForm()"
          class="px-4 py-2 text-sm rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">
          Cancelar
        </button>
        <button type="button" (click)="guardarTasa()" [disabled]="saving"
          class="px-4 py-2 text-sm rounded-lg font-medium bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </div>
  }

  <!-- Tabla de tasas -->
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
    @if (loading) {
      <div class="flex justify-center items-center py-10">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400 text-sm">Cargando tipos de cambio...</span>
      </div>
    } @else if (rates.length === 0) {
      <div class="text-center py-10 text-gray-500 dark:text-gray-400">
        <p class="text-sm">No hay tipos de cambio registrados.</p>
      </div>
    } @else {
      <div class="max-w-full overflow-x-auto">
        <table class="min-w-full">
          <thead class="border-b border-gray-100 dark:border-white/[0.05]">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Moneda</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Tipo de cambio</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Base</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Estado</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Última actualización</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-white/[0.05]">
            @for (rate of rates; track rate.id) {
              <tr>
                <td class="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                  {{ rate.currencyName }}
                  <span class="ml-1 text-xs font-mono text-gray-500 dark:text-gray-400">({{ rate.currencyCode }})</span>
                  @if (rate.currencySymbol) {
                    <span class="ml-1 text-xs text-gray-400">{{ rate.currencySymbol }}</span>
                  }
                </td>
                <td class="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300 font-mono">{{ rate.rateToBase }}</td>
                <td class="px-4 py-3 text-center">
                  @if (rate.isBase) {
                    <app-badge color="primary" size="sm">Base</app-badge>
                  } @else {
                    <span class="text-xs text-gray-400">-</span>
                  }
                </td>
                <td class="px-4 py-3 text-center">
                  <app-badge [color]="rate.isActive ? 'success' : 'error'" size="sm">
                    {{ rate.isActive ? 'Activa' : 'Inactiva' }}
                  </app-badge>
                </td>
                <td class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                  {{ rate.datetimeUpdated ? (rate.datetimeUpdated | date:'dd/MM/yyyy HH:mm') : '-' }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-center gap-1">
                    <button type="button" (click)="toggleActivo(rate)"
                      [disabled]="togglingId === rate.id || rate.isBase"
                      class="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                      [class.hover:text-success-600]="!rate.isActive"
                      [class.hover:text-error-600]="rate.isActive"
                      [title]="rate.isBase ? 'La moneda base no se puede desactivar' : (rate.isActive ? 'Desactivar' : 'Activar')">
                      @if (rate.isActive) {
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                        </svg>
                      } @else {
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      }
                    </button>
                    <button type="button" (click)="editarTasa(rate)"
                      class="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600" title="Editar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    <button type="button" (click)="eliminarTasa(rate)" [disabled]="rate.isBase"
                      class="rounded p-1.5 hover:bg-error-50 hover:text-error-600 text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                      [title]="rate.isBase ? 'No se puede eliminar el tipo de cambio de la moneda base' : 'Eliminar'">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>
